import React, { useState, useEffect } from 'react';
import { reportCardService } from '../../../services/index.js';
import { Plus, Trash2, RotateCcw, Save } from 'lucide-react';

const GradingSettings = ({ orgData, showSuccessNotification, showErrorNotification }) => {
  const [gradingSchemes, setGradingSchemes] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadGradingSchemes = async () => {
    setLoading(true);
    try {
      const data = await reportCardService.getGradingSchemes();
      if (Array.isArray(data) && data.length > 0) {
        setGradingSchemes(data);
      } else {
        // Fallback standard CBSE-style defaults
        setGradingSchemes([
          { grade: 'A1', minPercentage: 91.0, maxPercentage: 100.0, passStatus: 'PASS' },
          { grade: 'A2', minPercentage: 81.0, maxPercentage: 90.99, passStatus: 'PASS' },
          { grade: 'B1', minPercentage: 71.0, maxPercentage: 80.99, passStatus: 'PASS' },
          { grade: 'B2', minPercentage: 61.0, maxPercentage: 70.99, passStatus: 'PASS' },
          { grade: 'C1', minPercentage: 51.0, maxPercentage: 60.99, passStatus: 'PASS' },
          { grade: 'C2', minPercentage: 41.0, maxPercentage: 50.99, passStatus: 'PASS' },
          { grade: 'D', minPercentage: 33.0, maxPercentage: 40.99, passStatus: 'PASS' },
          { grade: 'E', minPercentage: 0.0, maxPercentage: 32.99, passStatus: 'FAIL' }
        ]);
      }
    } catch (err) {
      console.error('Failed to load grading schemes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate schemes
    for (let gs of gradingSchemes) {
      if (!gs.grade || gs.grade.trim() === '') {
        alert('Grade symbol cannot be empty.');
        return;
      }
      if (gs.minPercentage === undefined || gs.minPercentage < 0 || gs.minPercentage > 100 ||
          gs.maxPercentage === undefined || gs.maxPercentage < 0 || gs.maxPercentage > 100) {
        alert('Percentage values must be between 0 and 100.');
        return;
      }
      if (gs.minPercentage > gs.maxPercentage) {
        alert(`Min Percentage cannot be greater than Max Percentage for Grade ${gs.grade}.`);
        return;
      }
    }

    setLoading(true);
    try {
      const res = await reportCardService.saveGradingSchemes(gradingSchemes);
      if (res && res.success) {
        showSuccessNotification('Saved Successfully', 'Grading scheme policies updated');
        loadGradingSchemes();
      } else {
        showErrorNotification('Save Failed', res.error || 'Failed to update grading rules');
      }
    } catch (err) {
      showErrorNotification('Network Error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => {
    setGradingSchemes([...gradingSchemes, { grade: 'NEW', minPercentage: 0, maxPercentage: 0, passStatus: 'PASS' }]);
  };

  const deleteRow = (idx) => {
    setGradingSchemes(gradingSchemes.filter((_, i) => i !== idx));
  };

  const resetDefaults = () => {
    if (window.confirm('Reset scheme settings to CBSE standard defaults?')) {
      setGradingSchemes([
        { grade: 'A1', minPercentage: 91.0, maxPercentage: 100.0, passStatus: 'PASS' },
        { grade: 'A2', minPercentage: 81.0, maxPercentage: 90.99, passStatus: 'PASS' },
        { grade: 'B1', minPercentage: 71.0, maxPercentage: 80.99, passStatus: 'PASS' },
        { grade: 'B2', minPercentage: 61.0, maxPercentage: 70.99, passStatus: 'PASS' },
        { grade: 'C1', minPercentage: 51.0, maxPercentage: 60.99, passStatus: 'PASS' },
        { grade: 'C2', minPercentage: 41.0, maxPercentage: 50.99, passStatus: 'PASS' },
        { grade: 'D', minPercentage: 33.0, maxPercentage: 40.99, passStatus: 'PASS' },
        { grade: 'E', minPercentage: 0.0, maxPercentage: 32.99, passStatus: 'FAIL' }
      ]);
    }
  };

  useEffect(() => {
    loadGradingSchemes();
  }, []);

  return (
    <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e3a8a' }}>⚙️ Academic Grading Schemes</h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Define the percentage criteria, grade symbols, and pass/fail thresholds.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={addRow} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '8px 14px' }}>
            <Plus size={14} /> Add Row
          </button>
          <button onClick={resetDefaults} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '8px 14px' }}>
            <RotateCcw size={14} /> Reset Standard
          </button>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', fontSize: '14px' }}>Loading policies...</div>}

      {!loading && (
        <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', color: '#475569', textAlign: 'left', borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '12px 16px', fontWeight: '700' }}>Grade Symbol (e.g. A+)</th>
                <th style={{ padding: '12px 16px', fontWeight: '700' }}>Min Percentage (%)</th>
                <th style={{ padding: '12px 16px', fontWeight: '700' }}>Max Percentage (%)</th>
                <th style={{ padding: '12px 16px', fontWeight: '700' }}>Status</th>
                <th style={{ padding: '12px 16px', width: '80px', textAlign: 'center', fontWeight: '700' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {gradingSchemes.map((gs, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <td style={{ padding: '10px 16px' }}>
                    <input
                      type="text"
                      value={gs.grade}
                      onChange={(e) => {
                        const next = [...gradingSchemes];
                        next[idx].grade = e.target.value;
                        setGradingSchemes(next);
                      }}
                      style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100px', fontWeight: '700', fontSize: '13px', outline: 'none' }}
                    />
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <input
                      type="number"
                      step="0.01"
                      value={gs.minPercentage}
                      onChange={(e) => {
                        const next = [...gradingSchemes];
                        next[idx].minPercentage = parseFloat(e.target.value);
                        setGradingSchemes(next);
                      }}
                      style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '110px', fontSize: '13px', outline: 'none' }}
                    />
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <input
                      type="number"
                      step="0.01"
                      value={gs.maxPercentage}
                      onChange={(e) => {
                        const next = [...gradingSchemes];
                        next[idx].maxPercentage = parseFloat(e.target.value);
                        setGradingSchemes(next);
                      }}
                      style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '110px', fontSize: '13px', outline: 'none' }}
                    />
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <select
                      value={gs.passStatus}
                      onChange={(e) => {
                        const next = [...gradingSchemes];
                        next[idx].passStatus = e.target.value;
                        setGradingSchemes(next);
                      }}
                      style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', outline: 'none', cursor: 'pointer' }}
                    >
                      <option value="PASS">PASS</option>
                      <option value="FAIL">FAIL</option>
                    </select>
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                    <button
                      onClick={() => deleteRow(idx)}
                      style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px' }}
                      title="Remove Row"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {gradingSchemes.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>No grading rules configured. Click "Add Row" above.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
        <button
          onClick={handleSave}
          disabled={loading}
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 24px', fontSize: '13px', fontWeight: '700', background: '#2563eb' }}
        >
          <Save size={16} /> Save Configurations
        </button>
      </div>
    </div>
  );
};

export default GradingSettings;
